import { Chip } from "@firecms/ui";
import { useCallback, useMemo, useState } from "react";

import { fetchPractitioners, type ModMedPractitionerView } from "@/services/modmed";

import { CopyMenu } from "../components/CopyMenu";
import { Panel, PanelToolbar } from "../components/Panel";
import { QueryRowsInput } from "../components/QueryRowsInput";
import { EmptyRow, IdBadge, TableContainer, TBody, TD, TH, THead, TR } from "../components/Table";
import { usePanelFetch } from "../hooks";
import type { QueryRow } from "../types";
import { rowsToParams } from "../utils";

const DISABLED_REASON = "Save the document before fetching ModMed resources.";

const DEFAULT_ROWS: QueryRow[] = [
  { key: "_count", value: "50" },
  { key: "active", value: "true" },
];

const Dash = () => <span className="text-text-secondary dark:text-text-secondary-dark">—</span>;

type Props = { customerId?: string; disabled: boolean };

export const PractitionersPanel = ({ customerId, disabled }: Props) => {
  const [rows, setRows] = useState<QueryRow[]>(DEFAULT_ROWS);
  const [search, setSearch] = useState("");

  const fetcher = useCallback(
    () => fetchPractitioners(customerId!, rowsToParams(rows)),
    [customerId, rows],
  );
  const { state, run } = usePanelFetch<ModMedPractitionerView>(fetcher);

  const filtered = useMemo(() => {
    if (!state.results) return null;
    const q = search.trim().toLowerCase();
    if (!q) return state.results;
    return state.results.filter(
      (p) =>
        p.id.toLowerCase().includes(q) ||
        p.name.toLowerCase().includes(q) ||
        p.telecom.some((t) => t.value.toLowerCase().includes(q)),
    );
  }, [state.results, search]);

  return (
    <Panel
      title="Practitioners"
      count={state.results?.length}
      disabled={disabled}
      disabledReason={DISABLED_REASON}
      queryFilters={
        <QueryRowsInput
          rows={rows}
          onChange={setRows}
          suggestedKeys={["_count", "active", "name", "family", "given"]}
        />
      }
      toolbar={
        <PanelToolbar
          search={search}
          onSearchChange={setSearch}
          fetchLabel="Fetch practitioners"
          onFetch={run}
          disabled={disabled}
          state={state}
        />
      }
    >
      {filtered !== null && (
        <TableContainer>
          <THead>
            <tr>
              <TH className="w-28">ID</TH>
              <TH>Name</TH>
              <TH className="w-20">Active</TH>
              <TH className="w-44">Phone</TH>
              <TH className="w-52">Email</TH>
              <TH className="w-10" />
            </tr>
          </THead>
          <TBody>
            {filtered.length === 0 ? (
              <EmptyRow colSpan={6} label="practitioners" />
            ) : (
              filtered.map((p) => {
                const phone = p.telecom.find((t) => t.system === "phone")?.value;
                const email = p.telecom.find((t) => t.system === "email")?.value;
                return (
                  <TR key={p.id}>
                    <TD>
                      <IdBadge value={p.id} />
                    </TD>
                    <TD className="font-medium">{p.name}</TD>
                    <TD>
                      <Chip size="small" colorScheme={p.active ? "greenLighter" : "redLighter"}>
                        {p.active ? "Active" : "Inactive"}
                      </Chip>
                    </TD>
                    <TD className="text-sm">{phone ?? <Dash />}</TD>
                    <TD className="text-sm">{email ?? <Dash />}</TD>
                    <TD>
                      <CopyMenu
                        options={[
                          { label: "ID", value: p.id },
                          { label: "Name", value: p.name },
                          { label: "Phone", value: phone },
                          { label: "Email", value: email },
                        ]}
                      />
                    </TD>
                  </TR>
                );
              })
            )}
          </TBody>
        </TableContainer>
      )}
    </Panel>
  );
};
